import { autoGravity } from "@cloudinary/transformation-builder-sdk/qualifiers/gravity";
import { limitFill } from "@cloudinary/url-gen/actions/resize";
import { Button, Card, Heading, Spinner, Text } from "@ttc3k/trekker";
import { useOrganizationByIdQuery } from "gql/generated";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CloudinaryImage } from "shared/components";
import { getMultilingualString } from "shared/utils";
import { isLandscape } from "shared/utils/isLandscape";
import { css } from "styled-system/css";
import { Flex } from "styled-system/jsx";

type MyOrganizationSectionProps = {
  organizationId?: string;
};

export const MyOrganizationSection = ({
  organizationId,
}: MyOrganizationSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, loading, error } = useOrganizationByIdQuery({
    variables: { id: organizationId },
  });

  if (error) {
    throw new Error("Error loading organization data");
  }

  if ((!data || !data.organizationById) && !loading) {
    throw new Error("Cannot find organization");
  }

  const logo = data?.organizationById?.logo;

  return (
    <Flex flexDir={"column"} gap={"250"}>
      <Heading size={"h5"}>{t("directory:ORGANIZATION.MY")}</Heading>
      <Card.Surface
        bg={"white"}
        borderColor={"border.dark"}
        borderWidth={"2px"}
      >
        <Card.Body pl="300" pr="400" py={"300"}>
          <Card.BodyContent gap={"500"} alignItems={"center"}>
            {loading ? (
              <Spinner width={"20px"} height={"20px"} />
            ) : (
              <>
                {data && data.organizationById && (
                  <>
                    <Flex gap={"300"} alignItems={"center"}>
                      {logo && (
                        <CloudinaryImage
                          image={logo}
                          imageStyleProps={{
                            borderRadius: "100",
                            objectFit: "scale-down",
                            aspectRatio: "square",
                            className: css({ maxW: "100px", h: "100px" }),
                          }}
                          resize={
                            isLandscape(logo)
                              ? limitFill().height(100).gravity(autoGravity())
                              : limitFill().width(100).gravity(autoGravity())
                          }
                        />
                      )}
                      <Flex
                        flexDir={"column"}
                        gap={"25"}
                        pl={logo ? "300" : undefined}
                        borderLeft={logo ? "2px" : undefined}
                        borderColor={"border.light"}
                        borderStyle={"solid"}
                      >
                        <Text visual={"bodySemiBold"} color={"text.dark"}>
                          {getMultilingualString(data.organizationById.name)}
                        </Text>
                        <Text visual={"smallMedium"} color={"text.light"}>
                          {t("directory:ORGANIZATION.MY")}
                        </Text>
                      </Flex>
                    </Flex>
                    <Button
                      mode={"light"}
                      size={"lg"}
                      onClick={() =>
                        navigate(`/app/organization/${organizationId}/profile`)
                      }
                    >
                      {t("directory:ORGANIZATION.EDIT")}
                    </Button>
                  </>
                )}
              </>
            )}
          </Card.BodyContent>
        </Card.Body>
      </Card.Surface>
    </Flex>
  );
};
